/**
 * TEMA MUI — Design tokens centralizzati, parametrizzati per brand.
 *
 * IMPORTANTE: i colori della palette MUI (palette.primary.main, ecc.)
 * richiedono valori HEX espliciti — MUI createTheme NON accetta var(). Per il
 * white-label per-tenant il `primary` arriva dalla BrandPalette attiva (vedi
 * brandPalette.ts): `createAppTheme(palette)` ricostruisce il theme dai colori
 * del tenant. Gli altri colori (secondary/error/...) restano statici perché
 * non sono brand-specific.
 *
 * Tabella di corrispondenza (primary ← BrandPalette, gli altri ← _variables.scss):
 *   palette.primary       ← BrandPalette.primary/Light/Dark/onPrimary
 *   --color-secondary      → palette.secondary.main
 *   --color-error          → palette.error.main
 *   --color-bg             → palette.background.default/paper
 *   --color-text           → palette.text.primary
 *   --color-border         → palette.divider
 */

import { createTheme, type Theme } from '@mui/material/styles';
// Solo tipi: rende `components.MuiDataGrid` accettato da createTheme.
import type {} from '@mui/x-data-grid/themeAugmentation';
import { DEFAULT_PALETTE, mixSrgb, type BrandPalette } from './brandPalette';

// Arancione di stato (--color-warning in _variables.scss). Costante e non letterale come gli altri
// colori della palette perché serve due volte: nella palette e nella `variants` di MuiButton che lo
// ripropone dopo l'override dello slot `outlined`.
const WARNING_MAIN = '#b4671a';

// Font serif per i titoli (look editoriale del nuovo design). Inline e non
// var(): la typography MUI non manipola il valore, ma restiamo coerenti con la
// nota sopra evitando var() nel theme.
const SERIF_FONT =
  "'Iowan Old Style', 'Palatino Linotype', Palatino, 'Book Antiqua', Georgia, serif";

// Ombre morbide e bordo tenue del nuovo design (card che "galleggiano" sulla
// carta). Le card usano l'ombra corta; menu/popover una più diffusa.
const CARD_BORDER = '1px solid rgba(20, 19, 12, 0.08)';
const SOFT_SHADOW = '0 1px 2px rgba(20, 20, 15, 0.05), 0 6px 16px -10px rgba(20, 20, 15, 0.14)';
const FLOAT_SHADOW = '0 1px 2px rgba(20, 20, 15, 0.05), 0 14px 34px -16px rgba(20, 20, 15, 0.22)';

/** Costruisce il theme MUI dai colori brand del tenant attivo. Il secondo
 *  parametro (opzionale) è l'oggetto locale MUI X (es. itIT/enUS) che traduce
 *  i testi built-in del DataGrid. */
export function createAppTheme(palette: BrandPalette, dataGridLocale?: object): Theme {
  const baseTheme = createTheme({
  palette: {
    primary: { main: palette.primary, contrastText: palette.colorPrimary },
    secondary: { main: palette.secondary, light: mixSrgb(palette.secondary, '#ffffff', 0.2), contrastText: palette.colorSecondary },
    error: { main: '#b23a3a' },
    success: { main: '#2f7d52' },
    info: { main: '#4c6784' },
    warning: { main: WARNING_MAIN },
    // Superfici calde del nuovo design (menu/dialog/card MUI).
    background: {
      default: '#fcfcfb',
      paper: '#fcfcfb',
    },
    // Inchiostri caldi (coerenti con --color-text* in _variables.scss).
    text: {
      primary: '#1b1b18',
      secondary: '#565650',
      disabled: '#8a8a82',
    },
    divider: 'rgba(20, 19, 12, 0.12)',
  },
  typography: {
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: 14,
    // Titoli in serif; il corpo del testo resta sans.
    h1: { fontFamily: SERIF_FONT },
    h2: { fontFamily: SERIF_FONT },
    h3: { fontFamily: SERIF_FONT },
    h4: { fontFamily: SERIF_FONT },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    // styleOverrides: neutralizzano i default MUI che competono con le
    // classi CSS del progetto. Senza queste righe, le classi SCSS perdono
    // la battaglia di specificita' contro gli stili generati da MUI/Emotion.
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'small',
      },
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          // Bottoni a pill (nuovo design).
          borderRadius: 999,
          '&.Mui-disabled': { borderColor: 'transparent' },
        },
        // Bottoni secondari (variant="outlined"): bordo e testo scuri,
        // invece del primary giallo (basso contrasto su bianco).
        // NB: questo slot vale per OGNI outlined, colore incluso — in MUI v9 non ci sono più slot
        // colore-specifici (outlinedError e simili), quindi scrivere `color` qui in chiaro spegne
        // anche i colori semantici. Gli outlined che devono restare colorati si riportano su via
        // `variants` (oggi il solo warning, qui sotto): l'override resta il default, non la regola
        // unica.
        outlined: {
          color: '#131313',
          borderColor: 'rgba(0, 0, 0, 0.4)',
          '&:hover': {
            borderColor: '#131313',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
      // MUI v9 ha rimosso gli slot colore-specifici (es. containedPrimary): per
      // i bottoni primari (contained + color primary) usiamo l'API `variants`.
      // MUI pilota lo sfondo via la CSS var `--variant-containedBg`, che in
      // hover porterebbe a palette.primary.dark; la sovrascriviamo con
      // `--brand-strong`, lo stesso scurimento usato da `.sa--primary` e
      // `.btn-primary` nel design system.
      //
      // Prima qui c'era `var(--hover)`, cioe' `rgba(0,0,0,.04)`: uno sfondo
      // quasi trasparente sotto un testo che resta `contrastText` (bianco).
      // Su una superficie chiara — la modale di conferma, per dirne una — il
      // bottone spariva al passaggio del mouse.
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            border: '1px solid var(--primary)',
            '&:hover': {
              '--variant-containedBg': 'var(--brand-strong)',
              borderColor: 'var(--brand-strong)',
              boxShadow: 'none',
            },
          },
        },
        // Outlined semantico: ridà il colore che lo slot `outlined` qui sopra appiattisce, con le
        // proporzioni del default MUI (bordo al 50%, sfondo hover al 4%). Serve a Sospendi, dove
        // l'arancione dice "azione seria ma reversibile" e il grigio lo renderebbe indistinguibile da
        // un secondario qualunque. Un `outlined + error` NON c'è: le azioni distruttive di questa app
        // sono `contained + error` (rosso pieno), che lo slot non tocca.
        {
          props: { variant: 'outlined', color: 'warning' },
          style: {
            color: WARNING_MAIN,
            borderColor: `color-mix(in srgb, ${WARNING_MAIN} 50%, transparent)`,
            '&:hover': {
              borderColor: WARNING_MAIN,
              backgroundColor: `color-mix(in srgb, ${WARNING_MAIN} 4%, transparent)`,
            },
          },
        },
      ],
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none' as const },
      },
    },
    // Card come box che galleggiano: superficie calda, angoli morbidi, bordo
    // tenue, ombra soffusa. elevation 0 di default (l'ombra la diamo noi).
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#fcfcfb',
          border: CARD_BORDER,
          borderRadius: 18,
          boxShadow: SOFT_SHADOW,
        },
      },
    },
    // Niente gradient di elevazione MUI in dark-ish: superficie piatta calda.
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    // Menu/dropdown (utente, lingua, azioni): angoli morbidi + ombra diffusa +
    // bordo tenue, coerenti con le card del nuovo design.
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: CARD_BORDER,
          boxShadow: FLOAT_SHADOW,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: CARD_BORDER,
          boxShadow: FLOAT_SHADOW,
        },
      },
    },
    // Dialog/drawer come superfici morbide.
    //
    // FILO-27: lo sfondo delle popup è inerte. Un clic fuori chiudeva la popup e
    // scartava quanto scritto, senza chiedere; ora si chiude solo con X, Annulla
    // o Esc. Un punto solo per TUTTE le popup, core e plugin. Nel Dialog il
    // clic sullo sfondo lo gestisce l'onClick della root (handleBackdropClick, che
    // chiama onClose con 'backdropClick'), e `slotProps.root` è steso DOPO: un
    // onClick qui lo sostituisce. Gli slotProps del tema si fondono slot per slot
    // con quelli del singolo Dialog, quindi un `slotProps.paper` locale non lo
    // cancella. Da riverificare a ogni major di MUI: dipende da quell'ordine.
    MuiDialog: {
      defaultProps: { slotProps: { root: { onClick: () => {} } } },
      styleOverrides: { paper: { borderRadius: 18 } },
    },
    // Stesso motivo per i drawer temporanei, che passano da Modal: lì il clic
    // chiude dal Backdrop, e lo spegniamo togliendogli i puntatori. Il clic
    // arriva alla root del modal, che copre lo schermo e non ha un gestore di
    // chiusura: la pagina sotto resta bloccata. Menu e Popover non sono toccati.
    MuiDrawer: {
      styleOverrides: {
        modal: { '& > .MuiBackdrop-root': { pointerEvents: 'none' as const } },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
        fullWidth: true,
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
    },
    // Icona expand a sinistra invece che a destra (default MUI). Mantiene
    // la rotazione 180° gestita da MUI sull'expandIconWrapper.
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          flexDirection: 'row-reverse' as const,
        },
      },
    },
    // Default MUI mostra una linea di separazione `::before` sopra ogni
    // Accordion. Il design del progetto la nasconde sempre — globalizzato
    // qui per evitare la ripetizione di `sx={{ '&:before': { display: 'none' } }}`
    // su ogni call site (vedi RelationPanel, AddressListEditor, ActivityTimeline).
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': { display: 'none' },
        },
      },
    },
    // Hover delle righe di TUTTI i DataGrid: la sfumatura del brand delle liste SCSS. Il default MUI
    // (palette.action.hover, grigio) vinceva sulle righe dispari, quelle senza `even-row`, perché la
    // regola SCSS ha la sua stessa specificità: righe alternate grigie e gialline sotto il mouse.
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'color-mix(in srgb, var(--brand) 6%, var(--surface))',
          },
        },
      },
    },
  },
  });

  // Traduzioni built-in del DataGrid (footer paginazione "Rows per page", ecc.):
  // MUI X non passa da i18next, quindi le iniettiamo nel theme — vale per TUTTI i DataGrid.
  return dataGridLocale ? createTheme(baseTheme, dataGridLocale) : baseTheme;
}

/**
 * Theme di default (brand di default). Usato come fallback statico; a runtime
 * ThemeManager fornisce il theme costruito dalla palette del tenant loggato.
 */
export const theme = createAppTheme(DEFAULT_PALETTE);
